import React, { useState, useEffect } from 'react';
import { ReactComponent as BackIcon } from "../../../../assets/ChatbotReportsSvgs/BackButtonChatbotIcon.svg";
import { ReactComponent as CautionIconChatbotReportsIcon } from "../../../../assets/ChatbotReportsSvgs/CautionResponseChatbotReportsIcon.svg";
import './style.scss';
import { rawResultsInterface, ResultInterface } from '../../../../interface/ReportsChatbotInterface';

const PAGE_SIZE = 5;

interface DetailMessageBubbleProps {
    sender?: 'user' | 'ai';
    rawResults?: rawResultsInterface[] | Record<string, any>[] | null;
    /** New API shape: each entry renders its own table */
    results?: ResultInterface[];
    title?: string;
    isLoading?: boolean;
    isFailure?: boolean;
    apiFailureMessage?: string;
    onRetry?: () => void;
}

const formatColumnHeader = (key: string): string =>
    key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** A single paginated table rendered inside a detail bubble (own page state). */
const DetailTableCard: React.FC<{ rows: Record<string, any>[] }> = ({ rows }) => {
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setCurrentPage(1);
    }, [rows]);

    const headers = Object.keys(rows[0]);
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const start = (currentPage - 1) * PAGE_SIZE;
    const pagedRows = rows.slice(start, start + PAGE_SIZE);
    const showingFrom = start + 1;
    const showingTo = Math.min(currentPage * PAGE_SIZE, rows.length);
    const total = rows.length;

    return (
        <div className="detail-card">
            <div className="detail-table-wrapper">
                <table className="detail-table">
                    <thead>
                        <tr className="detail-card-header">
                            {headers.map((key, i) => (
                                <th key={i} className="detail-card-header-cell">
                                    {formatColumnHeader(key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="detail-card-body">
                        {pagedRows.map((row, idx) => (
                            <tr className="detail-card-row" key={idx}>
                                {headers.map((key, i) => (
                                    <td key={i} className={`detail-card-cell ${i === 0 ? 'detail-card-cell--label' : 'detail-card-cell--value'}`}>
                                        {((v) => v !== '' ? v : '–')(String((row as any)[key] ?? ''))}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="detail-pagination">
                    <span className="detail-pagination__info">
                        {showingFrom}–{showingTo} of {total}
                    </span>
                    <div className="detail-pagination__controls">
                        <button
                            className="detail-pagination__btn"
                            onClick={() => setCurrentPage(p => p - 1)}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                        >
                            <BackIcon className="detail-pagination__icon" />
                        </button>
                        <span className="detail-pagination__page">
                            {currentPage} / {totalPages}
                        </span>
                        <button
                            className="detail-pagination__btn"
                            onClick={() => setCurrentPage(p => p + 1)}
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                        >
                            <BackIcon className="detail-pagination__icon detail-pagination__icon--next" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const DetailMessageBubble: React.FC<DetailMessageBubbleProps> = ({
    sender: senderProp = 'ai',
    rawResults,
    results,
    isLoading = false,
    isFailure = false,
    apiFailureMessage = 'Unable to retrieve at the moment',
    onRetry
}) => {
    if (isLoading) {
        return (
            <div className="detail-message-row ai-message">
                <div className="detail-message-bubble detail-loading">
                    <div className="detail-typing-indicator">
                        <span /><span /><span />
                    </div>
                </div>
            </div>
        );
    }

    const showFailure = isFailure ?? false;

    if (showFailure) {
        return (
            <div className={`detail-message-row ${senderProp}-message`}>
                <div className="detail-message-bubble">
                    <div className="detail-failure-state">
                        <CautionIconChatbotReportsIcon className="detail-caution-icon" />
                        <span className="detail-failure-text">{apiFailureMessage}</span>
                        {onRetry && (
                            <button className="detail-retry-btn" type="button" onClick={onRetry}>
                                Retry
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // New API shape: render one bubble (table) per result object that has data
    if (Array.isArray(results) && results.length > 0) {
        const tables = results
            .map((result) => result?.raw_results)
            .filter((rows): rows is Record<string, any>[] => Array.isArray(rows) && rows.length > 0);

        if (tables.length === 0) {
            return (
                <div className={`detail-message-row ${senderProp}-message`}>
                    <div className="detail-message-bubble">
                        <span className="detail-no-data">No data available</span>
                    </div>
                </div>
            );
        }

        return (
            <>
                {tables.map((rows, idx) => (
                    <div className={`detail-message-row ${senderProp}-message`} key={idx}>
                        <div className="detail-message-bubble">
                            <DetailTableCard rows={rows} />
                        </div>
                    </div>
                ))}
            </>
        );
    }

    // Backward-compatible single table
    const hasData = Array.isArray(rawResults) && rawResults.length > 0;

    return (
        <div className={`detail-message-row ${senderProp}-message`}>
            <div className="detail-message-bubble">
                {hasData ? (
                    <DetailTableCard rows={rawResults as Record<string, any>[]} />
                ) : (
                    <span className="detail-no-data">No data available</span>
                )}
            </div>
        </div>
    );
};

export default DetailMessageBubble;
